import { cloneLevel, getLevel } from './levels';
import type {
  BrushMode,
  GameAction,
  GameState,
  Level,
  SerializedGameState,
} from './types';
import {
  applyBrushToGrid,
  clamp,
  clampBrushSize,
  hardHeightLimit,
} from './brush';

/**
 * Default camera orbit rotation (radians). `0` points the camera straight
 * down a grid axis (edge-on to the tile rows/columns); `PI/4` puts it at a
 * corner view, diagonal to both axes, which reads better in perspective and
 * avoids the axis-aligned viewing angle where same-row draw-order ties are
 * most visually apparent.
 */
export const DEFAULT_ROTATION = Math.PI / 4;

/**
 * Default camera focal length (FOV zoom); see SET_ZOOM. Paired with the
 * matching ~8x distance multiplier in camera.ts's ISOMETRIC_DISTANCE_MULTIPLIER
 * to flatten the default look toward isometric (near/far tile-size ratio
 * ~0.95, vs. ~0.71 at the old 1000/no-multiplier baseline).
 */
export const DEFAULT_ZOOM = 6000;
/** Default camera pitch in degrees from vertical; see SET_TILT. Matches the classic isometric elevation angle (arctan(1/sqrt(2))). */
export const DEFAULT_TILT = 35.26;
/** Zoom slider range: 0.6x-2.0x of DEFAULT_ZOOM, the same relative spread as the pre-iteration-43 600-2000/1000 range. */
export const MIN_ZOOM = 4800;
export const MAX_ZOOM = 16000;
export const MIN_TILT = 15;
export const MAX_TILT = 65;

export function createInitialState(level: Level = getLevel(0)): GameState {
  return {
    level,
    heights: [...level.heights],
    totalHeightChange: 0,
    rotation: DEFAULT_ROTATION,
    brushMode: 'lift',
    brushSize: 1,
    brushSpeed: 0.75,
    zoom: DEFAULT_ZOOM,
    tilt: DEFAULT_TILT,
    hoverTile: null,
    status: 'playing',
    failedBoundary: null,
    score: null,
    efficiency: null,
    message: 'Flatten the plane, then click Finish.',
  };
}

/**
 * Copies `previous`'s score/efficiency onto a freshly created state, so a
 * reset (of any kind) keeps showing the last attempt's outcome instead of
 * reverting the display to "-" until the next FINISH or failure computes a
 * new one. See the RESET/BRUSH/LOAD_LEVEL cases in reduceGame.
 */
function withPreservedScore(fresh: GameState, previous: GameState): GameState {
  return { ...fresh, score: previous.score, efficiency: previous.efficiency };
}

/**
 * Copies `previous`'s zoom/tilt camera settings onto a freshly created
 * state, so a camera preference persists across resets and level changes
 * as a matter of course. Unlike `withPreservedScore`, this is applied
 * unconditionally at every state-recreation site (including a deliberate
 * pick of a different level), since zoom/tilt are a camera preference, not
 * an outcome tied to a specific attempt.
 */
function withPreservedView(fresh: GameState, previous: GameState): GameState {
  return { ...fresh, zoom: previous.zoom, tilt: previous.tilt };
}

export function reduceGame(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'BRUSH': {
      // Painting after a finished attempt immediately starts a fresh one,
      // so the player can keep playing without an extra explicit reset step.
      // The just-finished score/efficiency stay displayed on the fresh
      // attempt until the next FINISH or failure recomputes them.
      const base =
        state.status === 'finished' ? withPreservedScore(createInitialState(cloneLevel(state.level)), state) : state;
      if (base.status !== 'playing') {
        return base;
      }

      const radius = clampBrushSize(action.size ?? base.brushSize, base.level);
      const limit = hardHeightLimit(base.level);
      const mode = action.mode ?? base.brushMode;
      const result = applyBrushToGrid(
        [...base.heights], base.level.size, action.x, action.y, mode, radius, action.intensity ?? 1, limit,
      );

      if (result.affectedIndices.length === 0) {
        return base;
      }

      if (result.failedBoundary) {
        const totalHeightChange = base.totalHeightChange + result.appliedChange;
        const score = computeScore(result.heights, base.level.heights);
        const efficiency = computeEfficiency(totalHeightChange, base.level.heights);
        return {
          ...base,
          heights: result.heights,
          totalHeightChange,
          status: 'failed',
          failedBoundary: result.failedBoundary,
          score,
          efficiency,
          message: `The plane collapsed at ${Math.round(score * 100)}% (efficiency ${Math.round(efficiency * 100)}%). Resetting…`,
        };
      }

      return {
        ...base,
        heights: result.heights,
        totalHeightChange: base.totalHeightChange + result.appliedChange,
        message: `${mode === 'lift' ? 'Lifted' : 'Lowered'} around (${action.x}, ${action.y}).`,
      };
    }
    case 'SET_BRUSH_MODE':
      return { ...state, brushMode: action.mode };
    case 'SET_BRUSH_SIZE':
      return { ...state, brushSize: clampBrushSize(action.size, state.level) };
    case 'SET_BRUSH_SPEED':
      return { ...state, brushSpeed: clamp(action.speed, 0.01, 1) };
    case 'SET_ZOOM':
      return { ...state, zoom: clamp(action.zoom, MIN_ZOOM, MAX_ZOOM) };
    case 'SET_TILT':
      return { ...state, tilt: clamp(action.tilt, MIN_TILT, MAX_TILT) };
    case 'SET_HOVER':
      return { ...state, hoverTile: action.tile };
    case 'ROTATE':
      return rotateView(state, action.direction, action.delta);
    case 'FINISH':
      return finishGame(state);
    case 'RESET':
      // A reset (manual button, fail-auto-reset, or the harness's reset())
      // restarts the current level's heights, but keeps showing the last
      // computed score/efficiency until a new FINISH or failure overwrites
      // them, instead of reverting the display to "-".
      return withPreservedView(withPreservedScore(createInitialState(cloneLevel(state.level)), state), state);
    case 'LOAD_LEVEL':
      // A deliberate level pick nulls the score (fresh level, fresh
      // context). preserveScore is set only when this LOAD_LEVEL is really
      // a "reset" re-roll of the currently loaded randomized level (see
      // performReset() in src/main.ts), in which case it behaves like RESET.
      // Zoom/tilt, however, are a camera preference and always carry over,
      // even for a deliberate pick of a different level.
      return action.preserveScore
        ? withPreservedView(withPreservedScore(createInitialState(action.level), state), state)
        : withPreservedView(createInitialState(action.level), state);
    case 'TIMEOUT':
      return timeoutGame(state);
    default:
      return state;
  }
}


/**
 * The ideal target height: the arithmetic mean of the level's initial heights.
 * This is the height at which the flat plane minimizes mean absolute deviation,
 * matching the scoring formula. Shown as a translucent sheet in the renderer.
 */
export function optimalHeight(initialHeights: number[]): number {
  if (initialHeights.length === 0) return 4;
  return initialHeights.reduce((sum, h) => sum + h, 0) / initialHeights.length;
}


/** Convenience wrapper: applies a brush stroke through the reducer (for tests and scripts). */
export function applyBrush(
  state: GameState,
  x: number,
  y: number,
  mode: BrushMode = state.brushMode,
  size: number = state.brushSize,
  intensity: number = 1,
): GameState {
  return reduceGame(state, { type: 'BRUSH', x, y, mode, size, intensity });
}

export function rotateView(state: GameState, direction?: 'cw' | 'ccw', delta?: number): GameState {
  const step = delta ?? (direction === 'ccw' ? -Math.PI / 6 : Math.PI / 6);
  const raw = state.rotation + step;
  const rotation = ((raw % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
  return { ...state, rotation };
}

export function standardDeviation(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

/** Mean absolute deviation from the mean: average of |value - mean(values)| across all values. Used as the "unevenness" measure for the perfection score, since it is a direct, height-agnostic measure of how far the plane is from flat. */
export function meanAbsoluteDeviation(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  const totalDeviation = values.reduce((sum, value) => sum + Math.abs(value - mean), 0);
  return totalDeviation / values.length;
}

function median(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Theoretical minimum total height change needed to flatten every cell to a
 * single common height, starting from `initialHeights`. Uses the median of
 * the initial heights as the L1-optimal common target — the flat plane can
 * end up at any height, so the efficiency baseline must be height-agnostic
 * too, unlike the old fixed `optimalHeight()` target.
 */
export function medianMinimalChange(initialHeights: number[]): number {
  const target = median(initialHeights);
  return initialHeights.reduce((sum, value) => sum + Math.abs(value - target), 0);
}

/**
 * Perfection score (0 to 1): how close the current heights are to a
 * perfectly flat plane, relative to how uneven the level started out.
 * 0 = exactly the level's initial random heights, 1 = perfectly flat at any
 * height. If the level already started perfectly flat, any state is scored
 * as perfect (there was nothing to flatten).
 */
export function computeScore(heights: number[], initialHeights: number[]): number {
  const initialDeviation = meanAbsoluteDeviation(initialHeights);
  if (initialDeviation <= 0) {
    return 1;
  }
  const currentDeviation = meanAbsoluteDeviation(heights);
  return clamp(1 - currentDeviation / initialDeviation, 0, 1);
}

/**
 * Efficiency (0 to 1), tracked separately from the perfection score: how
 * close the total height change applied over the course of play is to the
 * theoretical minimum needed to flatten the level (see
 * `medianMinimalChange`). 1 = no wasted strokes; smaller values mean more
 * height was moved than strictly necessary. If no change was theoretically
 * needed (already flat) and none was applied, efficiency is perfect (1).
 */
export function computeEfficiency(totalHeightChange: number, initialHeights: number[]): number {
  const minimalChange = medianMinimalChange(initialHeights);
  if (minimalChange <= 0) {
    return totalHeightChange > 0 ? 0 : 1;
  }
  return clamp(minimalChange / totalHeightChange, 0, 1);
}

export function finishGame(state: GameState): GameState {
  if (state.status !== 'playing') {
    return state;
  }

  const score = computeScore(state.heights, state.level.heights);
  const efficiency = computeEfficiency(state.totalHeightChange, state.level.heights);
  return {
    ...state,
    status: 'finished',
    score,
    efficiency,
    message: `Finished with score ${Math.round(score * 100)}% (efficiency ${Math.round(efficiency * 100)}%).`,
  };
}

/**
 * Fails the current attempt because its 5-minute time limit ran out (see
 * the TIMEOUT action and the play-time stopwatch in src/main.ts, which
 * tracks real elapsed time — a browser effect kept out of this pure
 * reducer). A no-op unless currently 'playing', mirroring the other
 * terminal actions (FINISH, height-boundary failures in applyBrush).
 */
export function timeoutGame(state: GameState): GameState {
  if (state.status !== 'playing') {
    return state;
  }

  const score = computeScore(state.heights, state.level.heights);
  const efficiency = computeEfficiency(state.totalHeightChange, state.level.heights);
  return {
    ...state,
    status: 'failed',
    failedBoundary: 'timeout',
    score,
    efficiency,
    message: `Time ran out! Score ${Math.round(score * 100)}% (efficiency ${Math.round(efficiency * 100)}%). Resetting…`,
  };
}

export function serializeState(state: GameState): SerializedGameState {
  return {
    levelId: state.level.id,
    size: state.level.size,
    heights: [...state.heights],
    totalHeightChange: state.totalHeightChange,
    rotation: state.rotation,
    brushMode: state.brushMode,
    brushSize: state.brushSize,
    brushSpeed: state.brushSpeed,
    zoom: state.zoom,
    tilt: state.tilt,
    hoverTile: state.hoverTile,
    status: state.status,
    failedBoundary: state.failedBoundary,
    score: state.score,
    efficiency: state.efficiency,
    message: state.message,
  };
}

